/**
 * useNotificationPreferences Hook
 * 
 * Handles notification preferences with local storage persistence.
 * Future: Will sync to server when notification settings API is available.
 */

import { useState, useEffect, useCallback } from 'react';
import { Platform } from 'react-native';
import * as SecureStore from 'expo-secure-store';

const STORAGE_KEY = 'notification_preferences';

export interface NotificationPreferences {
  pushEnabled: boolean;
  emailEnabled: boolean;
  mentionsEnabled: boolean;
  updatesEnabled: boolean;
  ddRemindersEnabled: boolean;
  taskRemindersEnabled: boolean;
  ideaNotificationsEnabled: boolean;
}

const DEFAULT_PREFERENCES: NotificationPreferences = {
  pushEnabled: true,
  emailEnabled: true,
  mentionsEnabled: true,
  updatesEnabled: true,
  ddRemindersEnabled: true,
  taskRemindersEnabled: true,
  ideaNotificationsEnabled: true,
};

async function getStoredPreferences(): Promise<NotificationPreferences | null> {
  try {
    let stored: string | null = null;
    
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined' && window.localStorage) {
        stored = localStorage.getItem(STORAGE_KEY);
      }
    } else {
      stored = await SecureStore.getItemAsync(STORAGE_KEY);
    }
    
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Silently fail
  }
  return null;
}

async function savePreferences(preferences: NotificationPreferences): Promise<void> {
  try {
    const value = JSON.stringify(preferences);
    
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(STORAGE_KEY, value);
      }
    } else {
      await SecureStore.setItemAsync(STORAGE_KEY, value);
    }
  } catch {
    // Silently fail
  }
}

interface UseNotificationPreferencesReturn {
  preferences: NotificationPreferences;
  isLoading: boolean;
  updatePreference: <K extends keyof NotificationPreferences>(
    key: K,
    value: NotificationPreferences[K]
  ) => void;
}

export function useNotificationPreferences(): UseNotificationPreferencesReturn {
  const [preferences, setPreferences] = useState<NotificationPreferences>(DEFAULT_PREFERENCES);
  const [isLoading, setIsLoading] = useState(true);

  // Load preferences on mount
  useEffect(() => {
    async function loadPreferences() {
      const stored = await getStoredPreferences();
      if (stored) {
        setPreferences({ ...DEFAULT_PREFERENCES, ...stored });
      }
      setIsLoading(false);
    }
    loadPreferences();
  }, []);

  const updatePreference = useCallback(<K extends keyof NotificationPreferences>(
    key: K,
    value: NotificationPreferences[K]
  ) => {
    setPreferences(prev => {
      const updated = { ...prev, [key]: value };
      // Save in background
      savePreferences(updated);
      return updated;
    });
  }, []);

  return {
    preferences,
    isLoading,
    updatePreference,
  };
}

